import { t } from "../../i18n";
import {
  Check,
  ChevronDown,
  ChevronRight,
  LoaderCircle,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react";
import { useState } from "react";
import type {
  PermissionEffect,
  PermissionRow,
  Person,
} from "../../pages/organization/UsersPage";
export function PersonPermissions({
  person,
  permissions,
  canWrite,
  isCurrentUser,
  saving,
  deleting,
  saved,
  onChange,
  onSave,
  onDelete,
}: {
  person: Person;
  permissions: PermissionRow[];
  canWrite: boolean;
  isCurrentUser: boolean;
  saving: boolean;
  deleting: boolean;
  saved: boolean;
  onChange: (
    permissionId: string,
    effect: PermissionEffect | "inherit",
  ) => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = person.permissionOverrides.length;
  const isAdmin = person.roles.some((role) => role.name === "admin");
  return (
    <section className="border-b border-stone-100 last:border-0 dark:border-white/5">
      <button
        type="button"
        className="flex w-full items-center gap-3 p-5 text-left hover:bg-stone-50 dark:hover:bg-white/3"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <div className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full bg-lumen-100 text-lumen-700 dark:bg-lumen-900/40 dark:text-lumen-300">
          {person.avatarUrl ? (
            <img
              src={person.avatarUrl}
              alt=""
              className="size-full object-cover"
            />
          ) : (
            <UserRound size={18} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{person.name}</span>
            {isAdmin && (
              <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-bold text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
                <ShieldCheck size={12} /> {t("Admin")}
              </span>
            )}
          </div>
          <div className="truncate text-sm text-stone-400">{person.email}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {person.roles
              .filter((role) => role.name !== "admin")
              .map((role) => (
                <span
                  key={role.id}
                  className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500 dark:bg-white/5 dark:text-stone-400"
                >
                  {role.name}
                </span>
              ))}
          </div>
        </div>
        <span className="rounded-full bg-stone-100 px-2.5 py-1 text-xs font-semibold text-stone-600 dark:bg-white/5 dark:text-stone-300">
          {t("override", { count: selected })}
        </span>
      </button>
      {open && (
        <div className="border-t border-stone-100 bg-stone-50/60 p-5 dark:border-white/5 dark:bg-black/10">
          <p className="mb-3 text-xs text-stone-500">
            {t(
              "Inherited access comes from {{roles}}. The status beside each permission shows the person’s effective access after overrides.",
              {
                roles: person.roles.length
                  ? person.roles.map((role) => role.name).join(", ")
                  : t("no assigned role"),
              },
            )}
          </p>
          <div className="grid gap-1 md:grid-cols-2">
            {permissions.map((permission) => {
              const effect = permission.id
                ? (person.permissionOverrides.find(
                    (override) => override.permissionId === permission.id,
                  )?.effect ?? "inherit")
                : "inherit";
              const inherited = Boolean(
                permission.id &&
                person.inheritedPermissionIds.includes(permission.id),
              );
              const effective = Boolean(
                permission.id &&
                person.effectivePermissionIds.includes(permission.id),
              );
              return (
                <label
                  key={permission.key}
                  className="flex items-start gap-3 rounded-xl p-3 hover:bg-white dark:hover:bg-white/4"
                >
                  <select
                    aria-label={`${permission.key} override`}
                    className={`mt-0.5 rounded-lg border px-2 py-1 text-xs font-semibold dark:border-white/10 dark:bg-stone-800 ${effect === "allow" ? "border-emerald-200 text-emerald-700 dark:text-emerald-300" : effect === "deny" ? "border-red-200 text-red-700 dark:text-red-300" : "border-stone-200 text-stone-500"}`}
                    disabled={!canWrite || !permission.id || saving || deleting}
                    value={effect}
                    onChange={(event) =>
                      permission.id &&
                      onChange(
                        permission.id,
                        event.target.value as PermissionEffect | "inherit",
                      )
                    }
                  >
                    <option value="inherit">{t("Inherit")}</option>
                    <option value="allow">{t("Allow")}</option>
                    <option value="deny">{t("Deny")}</option>
                  </select>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="block font-mono text-xs font-semibold">
                        {permission.key}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${effective ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300" : "bg-stone-200 text-stone-500 dark:bg-white/10 dark:text-stone-400"}`}
                      >
                        {effective ? "Has access" : "No access"}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-stone-400">
                      {permission.description}
                      {effect === "inherit"
                        ? ` · Inherited: ${inherited ? "allowed" : "not allowed"}`
                        : ""}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {canWrite && (
            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                {!isCurrentUser && (
                  <button
                    type="button"
                    className="button text-red-600 dark:text-red-300"
                    disabled={saving || deleting}
                    onClick={onDelete}
                  >
                    {deleting ? (
                      <LoaderCircle className="animate-spin" size={15} />
                    ) : (
                      <Trash2 size={15} />
                    )}{" "}
                    {t("Delete user")}
                  </button>
                )}
              </div>
              <button
                type="button"
                className="button-primary"
                disabled={saving || deleting}
                onClick={onSave}
              >
                {saving ? (
                  <LoaderCircle className="animate-spin" size={15} />
                ) : saved ? (
                  <Check size={15} />
                ) : (
                  <Save size={15} />
                )}{" "}
                {saved ? "Saved" : "Save access"}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
